import React, { useRef, useState } from 'react';
import PreventLostFocus from '../../../../../atoms/PreventLostFocus/PreventLostFocus';
import { useSonTranslation } from '../../../../../../translation/useSonTranslation';
import TermSuggestComboBox from './TermSuggestComboBox';
import { KeywordsEditableList } from '../../../../../../_lib/components/Keywords';
import { useHtmlValidId } from '../../../../../../hooks/useHtmlValidId';
import { TopKeywordResult } from '../AbstractComboBox/types/SuggestResultTypes';
import { sendLiveRegionMessage } from '../../../../../atoms/LiveRegionGlobal/LiveRegionGlobal';
import { Hint } from '../../../../../../_lib/components/Hint';

type TermSuggestComboBoxMultiSelect = {
  name: string;
  suggestUrl: string;
  label: string;
  helpText?: string;
  required?: boolean;
  value?: { id: string; title: string }[];
  maxLength?: number;
  maxElements?: number;
};

export function TermSuggestComboBoxMultiSelect({
  name,
  required = false,
  helpText,
  suggestUrl,
  maxLength,
  maxElements,
  label,
  value,
}: TermSuggestComboBoxMultiSelect) {
  const { t } = useSonTranslation('FormsComboBoxMultiSelect');
  const id = useHtmlValidId();
  const [keywords, setKeywords] = useState<TopKeywordResult[]>(value?.map(v => TopKeywordResult(v.id, v.title)) || []);

  const multiSelectRef = useRef(null);
  const removeKeyword = (item: TopKeywordResult) => {
    setKeywords(prevState => prevState.filter(keyword => keyword.id !== item.id));
    sendLiveRegionMessage(t('deleted', { item: item.title }));
  };

  const addKeyword = (item?: TopKeywordResult) => {
    if (item && !keywords.some(keyword => keyword.id === item.id)) {
      setKeywords(prevState => [...prevState, item]);
      sendLiveRegionMessage(t('selected', { item: item.title }));
    }
  };

  const mappedResults = keywords.map(item => ({
    name: item.title,
    onClick: () => removeKeyword(item),
  }));

  const maxElementsReached = typeof maxElements === 'number' && keywords.length >= maxElements;

  return (
    <PreventLostFocus attachTo={multiSelectRef}>
      <div ref={multiSelectRef} className="forms-combobox-multiselect">
        <TermSuggestComboBox
          id={`combobox-multiselect-terms-${id}`}
          required={required}
          maxLength={maxLength}
          labelTitle={label}
          suggestUrl={suggestUrl}
          ariaAutoComplete="list"
          labelHelpText={helpText}
          onSelection={item => {
            addKeyword(item);
          }}
          selectedItems={keywords}
          disabled={maxElementsReached}
        />
        {maxElementsReached && <Hint message={t('keywords.maxElementsHelpText', { maxElements })} />}
        <div className="forms-combobox-multiselect__keyword-resultlist">
          {keywords.map(keyword => (
            <input key={keyword.id} type="hidden" name={name} value={keyword.id} />
          ))}
          <KeywordsEditableList keywords={mappedResults} ariaLabel={t('Keywords:chosenKeywords')} />
        </div>
      </div>
    </PreventLostFocus>
  );
}
